/* eslint @typescript-eslint/no-explicit-any: "off" */
import React, { useCallback, useEffect, useState } from "react";
import { I18n } from "@aws-amplify/core";
import { Auth } from "aws-amplify";
import { Box, Card, Grid, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import { TotpEnrolment } from "../components/TotpEnrolment";
import { BasePage } from "./BasePage";

/**
 * Self-service two-factor enrolment for a signed-in user.
 *
 * This page is what makes a staged MFA rollout possible. While the user pool is
 * set to OPTIONAL, Cognito never issues an MFA_SETUP challenge, so there is no
 * other route to enrol -- without this, the only way to get anyone onto MFA would
 * be a hard cutover to REQUIRED for every admin and agent at once.
 *
 * Afterwards it is status-only. **There is deliberately no self-service device
 * replacement here.** Changing an authenticator is an administrator action for
 * every case, which keeps one procedure instead of two: self-service could only
 * ever have helped a user who still had their working device, and a lost or wiped
 * phone needs an administrator regardless -- that user cannot even complete
 * sign-in to reach this page. See docs/runbooks/mfa-reset.md.
 *
 * Do not reintroduce a replacement flow here by rendering TotpEnrolment for an
 * enrolled user. Mounting it calls Auth.setupTOTP, which mints a new secret; doing
 * that on page load made this page look like it was throwing signed-in users back
 * into setup. See docs/bugfix/2026-10-04-security-page-reenrolment-loop.md
 *
 * Enrolling here does not require a policy-compliant password. The two are
 * independent in Cognito, so a user still on an older, shorter password can
 * enrol today and change their password separately.
 */
export const SecurityPage: BasePage = () => {
  const [user, setUser] = useState<any>(null);
  const [enrolled, setEnrolled] = useState<boolean | null>(null);
  const [justEnrolled, setJustEnrolled] = useState(false);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(() => {
    Auth.currentAuthenticatedUser({ bypassCache: true })
      .then((current: any) => {
        setUser(current);
        return Auth.getPreferredMFA(current, { bypassCache: true });
      })
      .then((preferred: string) => {
        // "NOMFA" when nothing is registered; "SOFTWARE_TOKEN_MFA" once enrolled.
        setEnrolled(preferred === "SOFTWARE_TOKEN_MFA");
      })
      .catch(() =>
        setLoadError(
          I18n.get(
            "Could not read your two-factor status. Please reload the page."
          )
        )
      );
  }, []);

  useEffect(load, [load]);

  const onComplete = useCallback(() => {
    setJustEnrolled(true);
    setEnrolled(true);
  }, []);

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8} lg={6}>
          <Card variant="outlined">
            <Box p={3} display="flex" flexDirection="column">
              <Typography
                style={{ fontWeight: "bold", fontSize: "large" }}
                gutterBottom
              >
                {I18n.get("Two-factor authentication")}
              </Typography>

              {loadError && (
                <Alert variant="outlined" severity="error">
                  {loadError}
                </Alert>
              )}

              {justEnrolled && (
                <Alert variant="outlined" severity="success">
                  {I18n.get(
                    "Two-factor authentication is now on. You will be asked for a code from your authenticator app each time you sign in."
                  )}
                </Alert>
              )}

              {!loadError && enrolled === null && (
                <Typography>{I18n.get("Checking…")}</Typography>
              )}

              {enrolled === true && !justEnrolled && (
                <>
                  <Alert variant="outlined" severity="success">
                    {I18n.get(
                      "Two-factor authentication is on for your account."
                    )}
                  </Alert>

                  {/* Deliberately status-only: there is no self-service device
                      replacement. Changing an authenticator is an administrator
                      action, which is the single procedure for every case --
                      self-service could only ever have helped someone who still
                      had their working device, and a lost or wiped phone needs an
                      administrator regardless. See docs/runbooks/mfa-reset.md. */}
                  <Box mt={2}>
                    <Typography variant="body2">
                      {I18n.get(
                        "Changing or lost your phone? Contact an administrator — they will reset two-factor authentication for your account, and you will be asked to set up the new device the next time you sign in. Your password is not affected."
                      )}
                    </Typography>
                  </Box>
                </>
              )}

              {enrolled === false && (
                <>
                  <Typography gutterBottom>
                    {I18n.get(
                      "Protect your account with a code from your phone in addition to your password. This will become mandatory — setting it up now means nothing changes for you when it does."
                    )}
                  </Typography>
                  <Box mt={2}>
                    {user && (
                      <TotpEnrolment user={user} onComplete={onComplete} />
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};
